import { useNavigate } from 'react-router-dom';
import type { FinanceHotspot } from '@/mocks/finance';

interface FinanceHotspotCardProps {
  hotspot: FinanceHotspot & { isNew?: boolean };
  compact?: boolean;
  onShare?: (hotspot: FinanceHotspot) => void;
}

const heatConfig: Record<string, { icon: string; label: string }> = {
  '高': { icon: 'ri-fire-fill', label: '高热度' },
  '中': { icon: 'ri-fire-line', label: '中热度' },
  '低': { icon: 'ri-contrast-line', label: '一般' },
};

const tagColorMap: Record<string, string> = {
  '宏观政策': 'bg-blue-50 text-blue-700',
  '央行': 'bg-blue-50 text-blue-700',
  '科技与AI': 'bg-violet-50 text-violet-700',
  'AI应用': 'bg-violet-50 text-violet-700',
  '新能源': 'bg-emerald-50 text-emerald-700',
  '产业链': 'bg-emerald-50 text-emerald-700',
  '消费': 'bg-amber-50 text-amber-700',
  '政策': 'bg-blue-50 text-blue-700',
  '资金流向': 'bg-cyan-50 text-cyan-700',
  '港股与美股': 'bg-indigo-50 text-indigo-700',
  'A股': 'bg-rose-50 text-rose-700',
  '公司动态': 'bg-slate-100 text-slate-700',
};

export default function FinanceHotspotCard({ hotspot, compact, onShare }: FinanceHotspotCardProps) {
  const navigate = useNavigate();
  const heat = heatConfig[hotspot.heat] || heatConfig['低'];

  return (
    <button
      onClick={() => navigate(`/finance/${hotspot.id}`)}
      className="w-full text-left bg-background-50 rounded-xl px-4 py-4 border border-background-200/60 hover:border-accent-200 transition-all duration-200 cursor-pointer group"
    >
      {/* Header meta */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[10px] text-foreground-400">{hotspot.publishTime}</span>
        <span className="text-[10px] text-foreground-400">·</span>
        <span className="text-[10px] text-foreground-400">{hotspot.source}</span>
        <span className={`flex items-center gap-0.5 text-[10px] font-medium ${
          hotspot.heat === '高' ? 'text-red-500' : hotspot.heat === '中' ? 'text-amber-500' : 'text-foreground-400'
        }`}>
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={heat.icon}></i>
          </span>
          {heat.label}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-sm font-semibold text-foreground-800 mb-1.5 leading-snug group-hover:text-primary-600 transition-colors">
        {hotspot.isNew && (
          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-medium mr-1.5 align-middle">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-sparkling-line text-[10px]"></i>
            </span>
            新
          </span>
        )}
        {hotspot.title}
      </h3>

      {/* Summary */}
      <p className={`text-xs text-foreground-500 leading-relaxed mb-2.5 ${compact ? 'line-clamp-1' : 'line-clamp-2'}`}>
        {hotspot.summary}
      </p>

      {/* Tags & Direction */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {hotspot.tags.map((tag) => (
          <span
            key={tag}
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${tagColorMap[tag] || 'bg-secondary-100 text-secondary-600'}`}
          >
            {tag}
          </span>
        ))}
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-50 text-accent-600 font-medium whitespace-nowrap">
          {hotspot.impactDirection}
        </span>
      </div>

      {/* Actions */}
      {!compact && (
        <div className="flex items-center gap-3 mt-3 pt-2.5 border-t border-background-200/50">
          <span className="text-xs text-primary-500 font-medium flex items-center gap-1 hover:text-primary-600 transition-colors">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-article-line"></i>
            </span>
            查看详情
          </span>
          {onShare && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onShare(hotspot);
              }}
              className="text-xs text-secondary-500 font-medium flex items-center gap-1 hover:text-secondary-600 transition-colors cursor-pointer"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-share-forward-line"></i>
              </span>
              分享
            </span>
          )}
        </div>
      )}
    </button>
  );
}